import { ProductBoardingComponent } from './component/product-boarding/product-boarding.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Inject, Injectable, NgModule, Optional } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NativeDateAdapter } from '@angular/material/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LanguageService, SupportedLanguage } from './service/language.service';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { RouterModule } from '@angular/router';
import {
  OwlDateTimeIntl,
  OwlDateTimeModule,
  OWL_DATE_TIME_FORMATS,
  OWL_DATE_TIME_LOCALE,
} from '@danielmoncada/angular-datetime-picker';
import { OwlDayJsDateTimeModule } from '@danielmoncada/angular-datetime-picker-dayjs-adapter';
// FullCalendarModule removed - only used in admin/host pages

// Import dayjs with Korean locale for owl-date-time-picker
// The dayjs adapter uses standard 'dayjs' import, so we need to configure it here
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
dayjs.locale('ko');

import { NgSelectModule } from '@ng-select/ng-select';
import { PullToRefreshModule } from '@piumaz/pull-to-refresh';
import { LightgalleryModule } from 'lightgallery/angular/12';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { koLocale } from 'ngx-bootstrap/locale';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { RatingModule } from 'ngx-bootstrap/rating';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ClipboardModule } from 'ngx-clipboard';
import { NgxCronUiModule } from 'ngx-cron-ui';
import { TableModule } from 'ngx-easy-table';
import { ImageCropperModule } from 'ngx-image-cropper';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxMaskModule } from 'ngx-mask';
import { NgxPaginationModule } from 'ngx-pagination';
// QuillModule removed - using dynamic loading and plain HTML for display
import { TranslateModule } from '@ngx-translate/core';
import { SwiperModule } from 'swiper/angular';
import { environment } from './../environments/environment';
import { MessageTranslationToggleComponent } from './component/message-translation-toggle/message-translation-toggle.component';
// Dynamic Quill components are standalone and imported individually
import { DynamicQuillComponent } from './component/dynamic-quill/dynamic-quill.component';
import { CalendarHeader } from './component/calendar/calendar.component';
import { CouponComponent } from './component/coupon/coupon.component';
import { CronUiComponent } from './component/cron-ui/cron-ui.component';
import { MobileVerificationBottomSheetComponent } from './component/mobile-verification-bottom-sheet/mobile-verification-bottom-sheet.component';
import { HostInfoSectionEditComponent } from './component/host-info-section-edit/host-info-section-edit.component';
import { HostChangeReviewComponent } from './component/host-change-review/host-change-review.component';
import { EmptyComponent } from './component/empty/empty.component';
import { FeedComponent } from './component/feed/feed.component';
import { FormErrorsComponent } from './component/form-errors/form-errors.component';
import { FormInputComponent } from './component/form-input/form-input.component';
import { ThemePickerComponent } from './component/theme-picker/theme-picker.component';
import { AiImageEditOverlayComponent } from './component/ai-image-edit-overlay/ai-image-edit-overlay.component';
import { DescriptionImageEditorComponent } from './component/description-image-editor/description-image-editor.component';
import { ProductSearchSelectComponent } from './component/product-search-select/product-search-select.component';
import { CouponSearchSelectComponent } from './component/coupon-search-select/coupon-search-select.component';
import { UserSearchSelectComponent } from './component/user-search-select/user-search-select.component';
import { HeaderComponent } from './component/header/header.component';
import { SidebarLayoutComponent } from './component/sidebar-layout/sidebar-layout.component';
import { MapComponent } from './component/map/map.component';
import { MeetingControlComponent } from './component/meeting-control/meeting-control.component';
import { ReviewTicketGateComponent } from './component/review-ticket-gate/review-ticket-gate.component';
import { MeetingPreviewComponent } from './component/meeting-preview/meeting-preview.component';
import { NotionDescComponent } from './component/notion-desc/notion-desc.component';
import { PaginationComponent } from './component/pagination/pagination.component';
import { PopupComponent } from './component/popup/popup.component';
import { PriceComponent } from './component/price/price.component';
import { ProductCardComponent } from './component/product-card/product-card.component';
import { SegmentComponent } from './component/segment/segment.component';
import { ScheduleControlComponent } from './component/shedule-control/schedule-control.component';
import { ScheduleDetailComponent } from './component/shedule-detail/schedule-detail.component';
import { SnackBarComponent } from './component/snackbar/snackbar.component';
import { TicketParticipantsOptionComponent } from './component/ticket-participants-option/ticket-participants-option.component';
import { ParticipantCustomFieldEditorComponent } from './component/participant-custom-field-editor/participant-custom-field-editor.component';
import { EnvironmentBadgeComponent } from './component/environment-badge/environment-badge.component';
import { DebounceClickDirective } from './directive/debounce-click.directive';
import { InputDigitsOnlyDirective } from './directive/input.digits.only.directive';
import { LinkDirective } from './directive/link.directive';
import { ScrollDepthDirective } from './directive/scroll-depth.directive';
import { DateAgoPipe } from './pipe/date-ago.pipe';
import { RefundTotalPipe } from './pipe/refund-total.pipe';
import { IsCancelledPipe } from './pipe/is-cancelled.pipe';
import { SafeHtmlPipe } from './pipe/safe-html.pipe';
import { TruncatePipe } from './pipe/truncate.pipe';

export const MY_DAYJS_FORMATS = {
  parseInput: 'l LT',
  fullPickerInput: 'l LT',
  datePickerInput: 'l',
  timePickerInput: 'LT',
  monthYearLabel: 'MMM YYYY',
  dateA11yLabel: 'LL',
  monthYearA11yLabel: 'MMMM YYYY',
};

@Injectable({
  providedIn: 'root',
})
export class DefaultIntl extends OwlDateTimeIntl {
  cancelBtnLabel = '취소';
  setBtnLabel = '설정';
  rangeFromLabel = 'From';
  rangeToLabel = 'To';
}

defineLocale('ko', koLocale);

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

// Day of week names for each supported language
const DAY_OF_WEEK_NAMES: Record<SupportedLanguage, Record<'long' | 'short' | 'narrow', string[]>> = {
  ko: {
    long: ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
    short: ['일', '월', '화', '수', '목', '금', '토'],
    narrow: ['일', '월', '화', '수', '목', '금', '토'],
  },
  en: {
    long: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    short: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  },
  ja: {
    long: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'],
    short: ['日', '月', '火', '水', '木', '金', '土'],
    narrow: ['日', '月', '火', '水', '木', '金', '土'],
  },
};

@Injectable()
class CustomDateAdapter extends NativeDateAdapter {
  private currentLang: SupportedLanguage = 'ko';

  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string,
    @Optional() private languageService?: LanguageService,
  ) {
    super(matDateLocale);
    if (this.languageService) {
      this.languageService.currentLanguage$.subscribe((lang) => {
        this.currentLang = lang;
        this.setLocale(lang);
      });
    }
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return DAY_OF_WEEK_NAMES[this.currentLang]?.[style] || DAY_OF_WEEK_NAMES['ko'][style];
  }

  override getFirstDayOfWeek(): number {
    return 0;
  }

  override getDateNames(): string[] {
    // Korean/Japanese locale adds suffix (1일, 1日...), remove it for cleaner display
    if (this.currentLang === 'ko' || this.currentLang === 'ja') {
      return Array.from({ length: 31 }, (_, i) => String(i + 1));
    }
    return super.getDateNames();
  }
}

@NgModule({
  declarations: [
    TruncatePipe,
    DateAgoPipe,
    RefundTotalPipe,
    IsCancelledPipe,
    ProductCardComponent,
    MobileVerificationBottomSheetComponent,
    HostInfoSectionEditComponent,
    HostChangeReviewComponent,
    FormInputComponent,
    ThemePickerComponent,
    AiImageEditOverlayComponent,
    DescriptionImageEditorComponent,
    ProductSearchSelectComponent,
    CouponSearchSelectComponent,
    UserSearchSelectComponent,
    InputDigitsOnlyDirective,
    DebounceClickDirective,
    LinkDirective,
    ScrollDepthDirective,
    MeetingPreviewComponent,
    MeetingControlComponent,
    ReviewTicketGateComponent,
    FormErrorsComponent,
    NotionDescComponent,
    PriceComponent,
    PaginationComponent,
    MapComponent,
    EmptyComponent,
    FeedComponent,
    MessageTranslationToggleComponent,
    CouponComponent,
    SegmentComponent,
    HeaderComponent,
    SidebarLayoutComponent,
    ScheduleControlComponent,
    CronUiComponent,
    ScheduleDetailComponent,
    PopupComponent,
    CalendarHeader,
    SnackBarComponent,
    TicketParticipantsOptionComponent,
    ParticipantCustomFieldEditorComponent,
    EnvironmentBadgeComponent,
  ],
  imports: [
    ProductBoardingComponent,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    // QuillModule removed - using dynamic loading
    ClipboardModule,
    ModalModule.forRoot(),
    TableModule,
    NgxCronUiModule,
    // FullCalendarModule removed - only used in admin/host pages
    BsDatepickerModule.forRoot(),
    NgSelectModule,
    CarouselModule.forRoot(),
    NgxPaginationModule,
    OwlDateTimeModule,
    OwlDayJsDateTimeModule,
    RatingModule.forRoot(),
    TabsModule.forRoot(),
    ImageCropperModule,
    NgxMaskModule.forRoot(),
    SwiperModule,
    PullToRefreshModule,
    InfiniteScrollModule,
    MatBottomSheetModule,
    MatSnackBarModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatNativeDateModule,
    MatDatepickerModule,
    DragDropModule,
    LightgalleryModule,
    MatStepperModule,
    MatTooltipModule,
    ProgressbarModule.forRoot(),
    // Dynamic Quill components
    DynamicQuillComponent,
    // Safe HTML pipe for YouTube embeds and other HTML content
    SafeHtmlPipe,
    // i18n
    TranslateModule,
  ],
  exports: [
    ProductBoardingComponent,
    ProductCardComponent,
    MobileVerificationBottomSheetComponent,
    HostInfoSectionEditComponent,
    HostChangeReviewComponent,
    NotionDescComponent,
    FormInputComponent,
    ThemePickerComponent,
    AiImageEditOverlayComponent,
    DescriptionImageEditorComponent,
    ProductSearchSelectComponent,
    CouponSearchSelectComponent,
    UserSearchSelectComponent,
    PriceComponent,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TruncatePipe,
    DateAgoPipe,
    RefundTotalPipe,
    IsCancelledPipe,
    // QuillModule removed - using dynamic loading
    ClipboardModule,
    ModalModule,
    InputDigitsOnlyDirective,
    DebounceClickDirective,
    LinkDirective,
    ScrollDepthDirective,
    MeetingPreviewComponent,
    TableModule,
    MeetingControlComponent,
    ReviewTicketGateComponent,
    FormErrorsComponent,
    FeedComponent,
    NgxCronUiModule,
    // FullCalendarModule removed - only used in admin/host pages
    BsDatepickerModule,
    NgSelectModule,
    CarouselModule,
    NgxPaginationModule,
    PaginationComponent,
    OwlDateTimeModule,
    RatingModule,
    TabsModule,
    MapComponent,
    EmptyComponent,
    ImageCropperModule,
    NgxMaskModule,
    MessageTranslationToggleComponent,
    CouponComponent,
    SegmentComponent,
    SwiperModule,
    HeaderComponent,
    SidebarLayoutComponent,
    PullToRefreshModule,
    InfiniteScrollModule,
    ScheduleControlComponent,
    CronUiComponent,
    ScheduleDetailComponent,
    PopupComponent,
    MatBottomSheetModule,
    MatSnackBarModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatNativeDateModule,
    MatDatepickerModule,
    CalendarHeader,
    SnackBarComponent,
    DragDropModule,
    LightgalleryModule,
    MatStepperModule,
    MatTooltipModule,
    ProgressbarModule,
    TicketParticipantsOptionComponent,
    ParticipantCustomFieldEditorComponent,
    EnvironmentBadgeComponent,
    // Dynamic Quill components
    DynamicQuillComponent,
    // Safe HTML pipe for YouTube embeds and other HTML content
    SafeHtmlPipe,
    // i18n
    TranslateModule,
  ],
  providers: [
    { provide: OwlDateTimeIntl, useClass: DefaultIntl },
    { provide: OWL_DATE_TIME_FORMATS, useValue: MY_DAYJS_FORMATS },
    { provide: OWL_DATE_TIME_LOCALE, useValue: 'ko' },
    { provide: MAT_DATE_LOCALE, useValue: 'ko' },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, LanguageService],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class SharedModule {}
